import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyDetailsAndQuestions, SurveyDetailsAndQuestionsDTO } from "../types/surveyDetailsAndQuestions";
/* eslint-disable import/export */
export abstract class DeleteQuestionRPC implements
  LeoRPC<DeleteQuestionRPC.Request, DeleteQuestionRPC.Response, DeleteQuestionRPC.Errors.Errors>
{
  abstract execute(
    request: DeleteQuestionRPC.Request
  ): Promise<LeoRPCResult<DeleteQuestionRPC.Response, DeleteQuestionRPC.Errors.Errors>>;
}

export namespace DeleteQuestionRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    questionId: string;
    surveyId: string;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isDeleteQuestionRPCRequest: boolean = true;
  
    readonly questionId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
  
  
    constructor(
    questionId: LeoUUID, 
    surveyId: LeoUUID, 
    projectId: LeoUUID
    ) {
      this.questionId = questionId;
      this.surveyId = surveyId;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const questionId = getLeoUuid(dto, "questionId", InvalidRequestQuestionIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        questionId,
        surveyId,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          questionId: this.questionId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `DeleteQuestionRPC(questionId='${this.questionId}',surveyId='${this.surveyId}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyDetailsAndQuestions: SurveyDetailsAndQuestionsDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isDeleteQuestionResponse: boolean = true;
  
    readonly surveyDetailsAndQuestions: SurveyDetailsAndQuestions;
  
  
    constructor(
    surveyDetailsAndQuestions: SurveyDetailsAndQuestions
    ) {
      this.surveyDetailsAndQuestions = surveyDetailsAndQuestions;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyDetailsAndQuestions = SurveyDetailsAndQuestions.fromDTO(getObject(dto, "surveyDetailsAndQuestions", InvalidResponseSurveyDetailsAndQuestionsError));
      return new Response(
        surveyDetailsAndQuestions
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyDetailsAndQuestions: this.surveyDetailsAndQuestions.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `DeleteQuestion(surveyDetailsAndQuestions='${this.surveyDetailsAndQuestions}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidQuestionId = "INVALID_QUESTION_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    SurveyIsLive = "SURVEY_IS_LIVE"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `DeleteQuestionRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `DeleteQuestionRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidQuestionIdDTO {
      code: string;
    }
    
    export class InvalidQuestionId extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionId;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionIdDTO {
        const dto: InvalidQuestionIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionId.prototype.toString = function toString(): string {
      return `DeleteQuestionRPC.INVALID_QUESTION_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
      }
    
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `DeleteQuestionRPC.SURVEY_IS_CLOSED()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `DeleteQuestionRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface SurveyIsLiveDTO {
      code: string;
    }
    
    export class SurveyIsLive extends LeoRPCError<RPCError> {
      readonly isSurveyIsLive: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsLive;
        super(code);
      }
    
    
      toDTO(): SurveyIsLiveDTO {
        const dto: SurveyIsLiveDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsLive.prototype.toString = function toString(): string {
      return `DeleteQuestionRPC.SURVEY_IS_LIVE()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidProjectId | InvalidQuestionId | SurveyIsClosed | ProjectAlreadyArchived | SurveyIsLive;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidProjectIdDTO | InvalidQuestionIdDTO | SurveyIsClosedDTO | ProjectAlreadyArchivedDTO | SurveyIsLiveDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidQuestionId:
          return new InvalidQuestionId();
        case RPCError.SurveyIsClosed:
          return new SurveyIsClosed();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.SurveyIsLive:
          return new SurveyIsLive();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestQuestionIdError extends DeleteQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuestionIdError";
  }
}

export class InvalidRequestSurveyIdError extends DeleteQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends DeleteQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseSurveyDetailsAndQuestionsError extends DeleteQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyDetailsAndQuestionsError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends DeleteQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends DeleteQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidQuestionIdError extends DeleteQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends DeleteQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends DeleteQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseSurveyIsLiveError extends DeleteQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsLiveError";
  }
}

